import StandardBoxDTO from "@common/dtos/boxes/StandardBox";
import OpMachineDTO from "@common/dtos/OpMachineDTO";
import ProductListItemDTO from "@common/dtos/ProductListItemDTO";
import { MDBBtn, MDBCard, MDBCardBody, MDBCardFooter, MDBCardHeader, MDBIcon, MDBListGroup, MDBListGroupItem } from "mdb-react-ui-kit";
import { useMemo } from "react";
import { StatusIcon } from "../../../../../../../components/StatusIcon";
import { Trans, useTranslation } from "react-i18next";
import { MachineAction } from "../../../../../../../components/editable/MachineAction";
import API from "../../../../../../../service/api";
import { InputFieldEdit } from "../../../../../../../components/editable/InputFieldEdit";
import { CustomSelect } from "../../../../../../../components/CustomSelect";
import { ConfirmAction } from "../../../../../../../components/editable/ConfirmAction";

export interface StandardBoxViewerProps {
    machine: OpMachineDTO;
    box: StandardBoxDTO;
    apiURI: string;
    products: ProductListItemDTO[];
    canEditProduct: boolean;
    canEditPrice: boolean;
    canEditQnty: boolean;
    onClose: () => void;
}

export const StandardBoxViewer = ({
    machine,
    box,
    apiURI,
    canEditPrice,
    canEditProduct,
    canEditQnty,
    products,
    onClose
}: StandardBoxViewerProps) => {
    const { t } = useTranslation();

    const Header = useMemo(() => (
        <div className="d-flex flex-column w-100">
            <span className="d-flex w-100 justify-content-center align-items-center text-center" style={{fontWeight: 'bold'}}>
                {box.productName && <>
                    <span><MDBIcon icon="shopping-basket" color="primary" className="me-1"/></span>
                    {box.productName}
                </>}
                {!box.productName && <>
                    <span><MDBIcon icon="circle-question" color="warning" className="me-1"/></span>
                    <span className="text-warning">{t('unknown_product')}</span>
                </>}
            </span>
            {/* {box.status.length > 0 &&
                <span className="d-flex w-100 justify-content-center align-items-center" style={{flexWrap: "wrap"}}>
                    {box.status.map(s =>
                        <StatusIcon key={s.id} badge name={s.id} ts={s.ts}/>
                    )}
                </span>
            } */}
            <span className="d-flex w-100 justify-content-center align-items-center" style={{flexWrap: "wrap"}}>
                {box.status.map(s =>
                    <div key={s.id} style={{display: 'inline'}}>
                        <ConfirmAction
                            title={t('clear_status')}
                            confirmText={t('clear')}
                            prompt={<span><Trans i18nKey={'clear_status_prompt'} values={{statusTitle: t(`status:${s.id}`)}}/></span>}
                            action={async () => await API.POST(`${apiURI}/box/${box.id}/statusclear`, { status: s.id })}
                        >
                            { open => 
                                <StatusIcon badge name={s.id} ts={s.ts} onClick={open}/>
                            }
                        </ConfirmAction>

                    </div>
                )}
            </span>
            
        </div>
    ), [box, box.productName, box.status]);

    const Footer = useMemo(() => machine.bactions.length == 0 ? null : (
        <div className="d-flex flex-column">
            <span className="text-muted">{t('actions')}</span>
            <MDBListGroup light style={{maxHeight: 200, overflow: 'auto'}}>
                {machine.bactions.map(action => (
                    <MDBListGroupItem key={action.id} className="py-1 ps-2">
                        <MachineAction
                            action={action}
                            onDone={async () => await API.POST(`${apiURI}/box/${box.nr}/action`, { id: action.id })}
                        >
                            {open => (
                                <MDBBtn color="link" className="p-1" onClick={open}>
                                    <MDBIcon icon={action.icon || "wrench"} className="me-1" fixed/>
                                    <span>{t(action.title)}</span>
                                </MDBBtn>    
                            )}
                        </MachineAction>
                        
                    </MDBListGroupItem>
                ))}
            </MDBListGroup>
        </div>
    ), [machine, machine.bactions, apiURI, box, box.nr]);

    const Content = useMemo(() => (
        <MDBListGroup light>
            { typeof box.prodId == 'number' &&
                <MDBListGroupItem className="d-flex ps-3 py-2" noBorders>
                    <span className="d-block" style={{width: 150}}>
                        <MDBIcon icon="shopping-basket" fixed color="primary" className="me-1"/>
                        <span className="fw-bold me-1">{t('product')}</span>
                    </span>
                    <span className="d-flex flex-row align-items-center">
                        { !box.productName && <MDBIcon icon="circle-question" color="warning" className="me-1"/> }
                        <span className={"me-2" + (!box.productName ? ' text-warning' : '')}>{box.productName ?? t('unknown_product')}</span>
                        
                        <InputFieldEdit
                            canEdit={canEditProduct}
                            title={t('product')}
                            confirmText={t('update')}
                            value={box.prodId}
                            action={async pid => await API.PATCH(`${apiURI}/box/${box.nr}`, { pid })}
                            customInput={(crtValue, setCrtValue) => (
                                <CustomSelect
                                    title={t('product')} w100
                                    items={products.map((p) => ({
                                        val: p.id,
                                        title: p.name,
                                        icon: "fas fa-shopping-basket text-primary",
                                    }))}
                                    selected={crtValue}
                                    search={true}
                                    onSelect={setCrtValue}
                                />
                            )}
                        />
                    </span>
                </MDBListGroupItem>
            }
            { typeof box.price == 'number' &&
                <MDBListGroupItem className="d-flex ps-3 py-2" noBorders>
                    <span className="d-block" style={{width: 150}}>
                        <MDBIcon icon="money-bill" fixed color="primary" className="me-1"/>
                        <span className="fw-bold me-1">{t('price')}</span>
                    </span>
                    <span className="d-flex flex-row align-items-center">
                        <span className="me-2">{box.price}</span>

                        <InputFieldEdit
                            canEdit={canEditPrice}
                            type="number"
                            title={t('price')}
                            confirmText={t('update')}
                            value={box.price}
                            inputProps={{step: machine.mUnit, min: 0}}
                            inputLabel={t('price')}
                            inputTransformation={value => machine.mUnit * Math.round((value as number)/machine.mUnit)}
                            action={async price => await API.PATCH(`${apiURI}/box/${box.nr}`, { price: Number(price) })}
                        />
                    </span>
                </MDBListGroupItem>
            }
            { typeof box.qnty == 'number' &&
                <MDBListGroupItem className="d-flex ps-3 py-2" noBorders>
                    <span className="d-block" style={{width: 150}}>
                        <MDBIcon icon="shopping-cart" fixed color="primary" className="me-1"/>
                        <span className="fw-bold me-1">{t('quantity')}</span>
                    </span>
                    <span className="d-flex flex-row align-items-center">
                        <span className="me-2">{box.qnty}</span>
                        
                        <InputFieldEdit
                            canEdit={canEditQnty}
                            type="number"
                            title={t('quantity')}
                            confirmText={t('update')}
                            value={box.qnty}
                            inputLabel={t('quantity')}
                            action={async qnty => await API.PATCH(`${apiURI}/box/${box.nr}`, { qnty: Number(qnty) })}
                        />
                    </span>
                </MDBListGroupItem>
            }
        </MDBListGroup>
    ), [box, box.prodId, box.productName, box.nr, box.price, box.qnty, canEditPrice, canEditProduct, canEditQnty, apiURI, machine, products]);

    return (
        <div className="d-flex flex-column px-3 pt-3 flex-fill position-relative w-100">
            <MDBCard className={`w-100`} style={{}}>
                <div
                    className="position-absolute d-flex justify-content-center w-100"
                    style={{ top: 0, marginTop: -10 }}
                >
                    <span className="badge rounded-pill badge-primary">
                        {box.nr+1}
                    </span>
                </div>

                <MDBCardHeader className="w-100">
                    <span className="position-absolute" style={{fontWeight: 'bold'}}>
                        <MDBBtn tag="a" color="none" className="px-2 py-1" size="lg" onClick={onClose}><MDBIcon icon="chevron-left"/></MDBBtn>
                    </span>
                    <div className="d-flex flex-row justify-content-center align-items-center w-100">
                        { Header }
                    </div>
                    
                </MDBCardHeader>
                <MDBCardBody className="p-2">
                    { Content }
                </MDBCardBody>
                { Footer &&
                    <MDBCardFooter>
                        { Footer }
                    </MDBCardFooter>
                }
            </MDBCard>
        </div>
    )
}